import postRobot from 'post-robot'
import { AddToCartResponse, GetProductsResponse } from './WholesalerPublicApi'
import GetProductsResponseJsonSchema from './schema/GetProductsResponse.json'
import AddToCartResponseJsonSchema from './schema/AddToCartResponse.json'
import GetProductsPayloadJsonSchema from './schema/GetProductsPayload.json'
import AddToCartPayloadJsonSchema from './schema/AddToCartPayload.json'
import ConnectResponseJsonSchema from './schema/ConnectResponse.json'
import WarningMessagePayloadJsonSchema from './schema/WarningMessagePayload.json'

import {
  AddToCartPayload,
  ConnectResponse,
  GetProductsPayload,
  WarningMessagePayload,
  WholesalerConfiguration,
} from './WholesalerInternalApi'
import { validateAgainstSchema } from './validationUtils'

export function assertGetProductsResponse(payload: unknown): asserts payload is GetProductsResponse {
  validateAgainstSchema(payload, GetProductsResponseJsonSchema)
}

export function assertGetProductsPayload(payload: unknown): asserts payload is GetProductsPayload {
  validateAgainstSchema(payload, GetProductsPayloadJsonSchema)
}

export function assertAddToCartResponse(payload: unknown): asserts payload is AddToCartResponse {
  validateAgainstSchema(payload, AddToCartResponseJsonSchema)
}

export function assertAddToCartPayload(payload: unknown): asserts payload is AddToCartPayload {
  validateAgainstSchema(payload, AddToCartPayloadJsonSchema)
}

export function assertConnectResponse(payload: unknown): asserts payload is WholesalerConfiguration {
  validateAgainstSchema(payload, ConnectResponseJsonSchema)
}

export function assertWarningMessagePayload(payload: unknown): asserts payload is WholesalerConfiguration {
  validateAgainstSchema(payload, WarningMessagePayloadJsonSchema)
}

/**
 * Definitions of the messages passed between the Builder iframe and the wholesaler client script
 */
export interface WholesalerApiMethods {
  'IconicBuilder.Wholesaler.Connect'(payload: undefined): ConnectResponse
  'IconicBuilder.Wholesaler.Price'(payload: GetProductsPayload): GetProductsResponse
  'IconicBuilder.Wholesaler.AddToCart'(payload: AddToCartPayload): AddToCartResponse
  'IconicBuilder.Wholesaler.GoToCart'(payload: undefined): void
  'IconicBuilder.Wholesaler.Close'(payload: undefined): void
  'IconicBuilder.Wholesaler.Warning'(payload: WarningMessagePayload): void
}

/**
 * Type of the first argument of a function type, or never
 */
export type PayloadType<F> = F extends (arg: infer P) => any ? P : never

/**
 * Creates a type-safe wrapper for sending a message over postMessage with post-robot,
 *  based on the definitions in {@link WholesalerApiMethods}
 *
 * @see handleWholesalerApiCall for receiving end
 *
 * Given a method name from {@link WholesalerApiMethods}, the data payload type and return type are inferred.
 * A runtime validation function must also be supplied to ensure type-safety.
 *
 * E.g. for a method 'IconicBuilder.Wholesaler.Example'(payload: ExamplePayloadType): ExampleReturnType
 * This would result in the following specialisation:
 *   makeWholesalerApiCall(
 *     methodName: 'IconicBuilder.Wholesaler.Example',
 *     payload: ExamplePayloadType,
 *     resultTypeAsserter: ((input: unknown) => asserts input is ExampleReturnType)
 *   ): Promise<ExampleReturnType>
 *
 * @param methodName name of a method in {@link WholesalerApiMethods}
 * @param payload data to be sent (type inferred from method name)
 * @param resultTypeAsserter an assertion function that validates the return value (type inferred from method name)
 * @param timeout maximum time to wait for a response
 */
export async function makeWholesalerApiCall<METHOD_NAME extends keyof WholesalerApiMethods>(
  methodName: METHOD_NAME,
  payload: PayloadType<WholesalerApiMethods[METHOD_NAME]>,
  resultTypeAsserter: ((param: unknown) => asserts param is ReturnType<WholesalerApiMethods[METHOD_NAME]>),
  timeout: number,
): Promise<ReturnType<WholesalerApiMethods[METHOD_NAME]>> {
  const response = await postRobot.send(window.parent, methodName, payload, { timeout })
  const data: unknown = response.data

  resultTypeAsserter(data)

  return data
}

/**
 * Creates a type-safe wrapper for receiving a message over postMessage with post-robot,
 *  based on the definitions in {@link WholesalerApiMethods}
 *
 * @see makeWholesalerApiCall for sending end
 *
 * Given a method name from {@link WholesalerApiMethods}, the expected data payload type and return type are inferred.
 * A handler function that returns the expected result type,
 *  and a runtime validation function must also be supplied to ensure type-safety.
 *
 * E.g. for a method 'IconicBuilder.Wholesaler.Example'(payload: ExamplePayloadType): ExampleReturnType
 * This would result in the following specialisation:
 *   handleWholesalerApiCall(
 *     methodName: 'IconicBuilder.Wholesaler.Example',
 *     payloadTypeAsserter: ((input: unknown) => asserts input is ExamplePayloadType)
 *     handler: (payload: ExamplePayloadType) => Promise<ExampleReturnType>,
 *   )
 *
 * @param methodName name of a method in {@link WholesalerApiMethods}
 * @param payloadTypeAsserter an assertion function that validates the input value (type inferred from method name)
 * @param handler message handler to receive the data and return a result (type inferred from method name)
 *
 * @returns cancelable object
 */
export function handleWholesalerApiCall<METHOD_NAME extends keyof WholesalerApiMethods>(
  methodName: METHOD_NAME,
  payloadTypeAsserter: (input: unknown) => asserts input is PayloadType<WholesalerApiMethods[METHOD_NAME]>,
  handler: (payload: PayloadType<WholesalerApiMethods[METHOD_NAME]>) => Promise<ReturnType<WholesalerApiMethods[METHOD_NAME]>>
): { cancel: () => void } {
  return postRobot.on(methodName, (event) => {
    const data: unknown = event.data

    payloadTypeAsserter(data)

    return handler(data)
  })
}